import * as React from "react";
import { ReactNode } from "react";
import { calculateTooltipPosition } from "../HighlightModal/HighlightModal.utils";

interface ClickedContent {
  scrollX: number;
  scrollY: number;
  left: number;
  top: number;
  highLightText: string;
  highLightTextHeight: number;
  arrowPosition: string;
  arrowOffset?: number;
}

interface ProviderState {
  visible: boolean;
  clickedContent?: ClickedContent;
  contentId: string;
  contentTitle?: string;
  contentDescription?: string;
  contentImage?: string;
}

interface HighlightContextValue {
  state: ProviderState;
  onCloseHighlightModal: () => void;
  onPlainTextClick: (
    contentId: string,
    event: React.MouseEvent | MouseEvent,
    contentTitle?: string,
    contentDescription?: string,
    contentImage?: string
  ) => void;
}

interface ComponentProps {
  children: ReactNode;
}

const HighlightContext = React.createContext<HighlightContextValue | undefined>(
  undefined
);

export function useHighlight(): HighlightContextValue {
  const context = React.useContext(HighlightContext);
  if (context === undefined) {
    throw new Error("useHighlight must be used within HighlightProvider");
  }
  return context;
}

export function HighlightProvider({
  children,
}: ComponentProps): React.ReactElement {
  const [state, setState] = React.useState<ProviderState>({
    visible: false,
    clickedContent: undefined,
    contentId: "",
  });

  const onPlainTextClick = React.useCallback(
    (
      contentId: string,
      event: React.MouseEvent | MouseEvent,
      contentTitle?: string,
      contentDescription?: string,
      contentImage?: string
    ): void => {
      const viewportWidth = window.innerWidth;
      const nativeEvent = "nativeEvent" in event ? event.nativeEvent : event;
      const {
        scrollX,
        scrollY,
        modalTop: top,
        modalLeft: left,
        highLightText,
        highLightTextHeight,
        arrowPosition,
        arrowOffset,
      } = calculateTooltipPosition(nativeEvent);
      setState({
        contentId,
        contentTitle,
        contentDescription,
        contentImage,
        visible: true,
        clickedContent: {
          scrollX,
          scrollY,
          left,
          top,
          highLightText,
          highLightTextHeight,
          arrowPosition,
          arrowOffset,
        },
      });
    },
    []
  );

  const onCloseHighlightModal = React.useCallback((): void => {
    setState({
      visible: false,
      clickedContent: undefined,
      contentId: "",
    });
  }, []);

  const value = React.useMemo(
    () => ({ state, onCloseHighlightModal, onPlainTextClick }),
    [state, onCloseHighlightModal, onPlainTextClick]
  );

  return (
    <HighlightContext.Provider value={value}>
      {children}
    </HighlightContext.Provider>
  );
}
